/* eslint-disable max-lines-per-function */
import type { Maybe } from '@adornis/base/utilTypes.js';
import { selectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { AdminInstitutionViewSetting } from '@adornis/buchungsheld-db/entities/AdminInstitutionViewSetting.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { Einzelberatung } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import { dataviewEmptyState } from '@adornis/buchungsheld-db/global-helpers.js';
import { EinzelberatungDataview } from '@adornis/buchungsheld-db/use-case-entities/EinzelberatungDataview.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { DesignSystem, type ThemeDefinition } from '@adornis/chemistry/elements/theming/design.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { watch } from '@lit-labs/preact-signals';
import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { useContextEntity } from '../../../../util.js';
import './bh-page--admin-einrichtung-counseling-modify.js';
import './bh-page-admin-einrichtung-counseling-detail.js';
import './bh-page-admin-einrichtung-counseling-edit.js';

const getLsKey = (institutionID: string) => `extra_columns:${institutionID}:counselings:v1`;

function getCachedExtraColumns(institutionID: string) {
  const lsKey = getLsKey(institutionID);
  const extraColumns = JSON.parse(localStorage.getItem(lsKey) ?? '[]');
  return extraColumns;
}

function setCachedExtraColumns(institutionID: string, columns: string[]) {
  const lsKey = getLsKey(institutionID);
  localStorage.setItem(lsKey, JSON.stringify(columns));
}

function onViewUpdate(institutionID: string, dataViewOptions: DataViewOptions<any>, view: View<any>) {
  const preExistingFields = dataViewOptions.customColumns!.map(col => col.fieldName).filter(isDefined);
  const cachedExtraFields = getCachedExtraColumns(institutionID);
  const selectionFields = view.configuredColumns!.map(col => col.fieldName).filter(isDefined);
  const newFields = selectionFields.filter(it => !preExistingFields.includes(it));
  const fieldsToBeRemoved = preExistingFields.filter(it => !selectionFields.includes(it));
  const newCachedExtraFields = cachedExtraFields.filter(it => !fieldsToBeRemoved.includes(it));
  for (const field of newFields) newCachedExtraFields.push(field);

  setCachedExtraColumns(institutionID, newCachedExtraFields);

  view.configuredColumns?.sort((col1, col2) => {
    if (col1.customID === '_select') return -1;
    if (col1.customID === '_options') return 1;
    if (col2.customID === '_select') return 1;
    if (col2.customID === '_options') return -1;
    return 0;
  });
}

export const adminEinrichtungCounselingsRouter = makeRouter(
  {
    root: {
      path: '/',
      metaData: {
        breadcrumbs: ({ params }) => [{ content: 'Einzelberatungen', active: true }],
        semesterSelect: true,
      },
      render: params => html`${renderListing(params)}`,
    },
    add: {
      path: '/add*',
      metaData: {
        breadcrumbs: ({ params }) => [
          { content: 'Einzelberatungen', href: `/admin/institution-details/${params.institutionID}/counselings` },
          { content: 'Einzelberatung anlegen', active: true },
        ],
      },
      render: params => html`
        <bh-page-admin-einrichtung-counseling-modify
          institution-id=${params.institutionID}
          mode="add"
        ></bh-page-admin-einrichtung-counseling-modify>
      `,
    },

    detail: {
      path: '/detail/:counselingID',
      metaData: {
        breadcrumbs: ({ params }) => [
          { content: 'Einzelberatungen', href: `/admin/institution-details/${params.institutionID}/counselings` },
          { content: 'Detail', active: true },
        ],
      },
      render: params =>
        html`
          <bh-page-admin-einrichtung-counseling-detail
            counseling-id=${params.counselingID}
            institution-id=${params.institutionID}
          ></bh-page-admin-einrichtung-counseling-detail>
        `,
    },
  },
  {
    fallback: params => html` <x-empty-placeholder .img=${'close'} .text=${'404'} no-wrapper></x-empty-placeholder> `,
  },
);

async function saveFilter(
  viewSettings: Maybe<
    NarrowedEntity<AdminInstitutionViewSetting, { institutionDetailsCounselingsFilterV1: typeof BHFilter.allFields }>
  >,
  filter: any,
) {
  if (!viewSettings) return;
  viewSettings.institutionDetailsCounselingsFilterV1 = filter;
  await viewSettings.save();
}

const renderListing = stateful(params => {
  const design = useObservable(() => DesignSystem.currentTheme, []);
  const [viewSettings] = useContextEntity(AdminInstitutionViewSetting)({
    _id: 1,
    selectedTimeframeID: 1,
    institutionDetailsCounselingsFilterV1: selectionSet(() => BHFilter, 5),
  });

  console.log({ viewSettings, params });

  const institutionID = params.institutionID;
  if (!institutionID) return html` <tr-loading-state></tr-loading-state> `;

  const filter = new BHFilter({
    institutionID,
    timeframeID: viewSettings?.selectedTimeframeID,
    searchFields: ['name', 'description'],
    sortingFields: ['sortingWeight'],
    sortingDirections: ['asc'],
  });

  // Get saved filter values
  const savedAspects = viewSettings?.institutionDetailsCounselingsFilterV1?.aspects;
  const savedSortFields = viewSettings?.institutionDetailsCounselingsFilterV1?.sortingFields;
  const savedSortDirections = viewSettings?.institutionDetailsCounselingsFilterV1?.sortingDirections;

  // Only set values if they exist and have length > 0
  if (savedAspects && savedAspects.length > 0) filter.aspects = savedAspects as any;
  if (savedSortFields && savedSortFields.length > 0) filter.sortingFields = savedSortFields as any;
  if (savedSortDirections && savedSortDirections.length > 0) filter.sortingDirections = savedSortDirections as any;

  return html`
    ${permissionGuard(
      Permission.EDIT_ADMIN_INSTITUTION_COUNSELINGS,
      () => {
        // Add cached extra columns
        const extraColumns = getCachedExtraColumns(institutionID).map(fieldName => ({ fieldName }));

        const { dataViewOptions, filterOptions } = buildFullOptions({
          class: EinzelberatungDataview,
          operation: 'subscribeEinzelberatungDataview',
          view: new View({ filter: filter }),
          onViewChanged: async view => {
            onViewUpdate(institutionID, dataViewOptions, view);
            // TODO: save filter in onSortUpdate Event or somehow ignore dataview update
            // await saveFilter(viewSettings, view.filter);
          },
          isColumnSelectable: true,
          isEntryClickable: true,
          isSortable: true,
          emptyState: () => dataviewEmptyState({}),
          customColumns: [
            ...extraColumns,
            {
              fieldName: 'category.name',
              columnName: 'Kategorie',
              selectionSet: EinzelberatungDataview.neededGqlFields,
            },
            { fieldName: 'einzelberatung.name', sortable: true, columnName: 'Name' },
            {
              columnName: clientTranslator.translate('field', 'start'),
              customID: 'start',
              sortable: true,
              getFieldMapping: instance =>
                instance.einzelberatung.slotConfig?.start.toLocaleString(DateTime.DATETIME_MED),
            },
            {
              columnName: clientTranslator.translate('field', 'end'),
              customID: 'end',
              sortable: true,
              getFieldMapping: instance =>
                instance.einzelberatung.slotConfig?.end.toLocaleString(DateTime.DATETIME_MED),
            },
            {
              columnName: clientTranslator.translate('counseling', 'duration_per_slot'),
              customID: 'slotDuration',
              sortable: true,
              getFieldMapping: instance => `${instance.einzelberatung.slotConfig?.slotDuration} Min`,
            },
            {
              columnName: '#' + (clientTranslator.translate('counseling', 'participant') as string),
              fieldName: 'einzelberatung.participationCount',
              sortable: true,
              selectionSet: { 'einzelberatung.participationCount': 1 },
            },
            {
              columnName: '#' + (clientTranslator.translate('counseling', 'slots') as string),
              customID: 'numSlots',
              sortable: true,
              getFieldMapping: instance => instance.einzelberatung.slots.length,
            },
            {
              columnName: 'Reihenfolge',
              customID: 'sortingWeight',
              columnWidth: 'max-content',
              renderItem: instance =>
                html` <tr-text align="right"> ${instance.einzelberatung.sortingWeight} </tr-text> `,
            },
            {
              columnName: '',
              customID: '_options',
              renderItem: instance => renderOptions(instance, design, params),
              columnWidth: 'max-content',
            },
          ] as Array<ColumnDefinition<typeof EinzelberatungDataview>>,
        } satisfies DataViewOptions<typeof EinzelberatungDataview>);
        return html`
          <x-flex space="md" ${css({ alignItems: 'flex-start' })}>
            <tr-button-small
              icon="add"
              ${css({ width: 'min-width' })}
              .action=${() => goTo(`/admin/institution-details/${institutionID}/counselings/add`)}
            ></tr-button-small>
            <x-filter-editor
              .options=${filterOptions}
              @filter-apply=${async e => saveFilter(viewSettings, e.detail)}
              @filter-reset=${() => saveFilter(viewSettings, null)}
            ></x-filter-editor>
            <x-data-view
              .options=${dataViewOptions}
              @entry-clicked=${(e: CustomEvent) => {
                console.log('clicked', e.detail, e.detail);
                const counselingID = e.detail.entity.einzelberatung._id;
                goTo(
                  `/admin/institution-details/${institutionID}/counselings/detail/${counselingID}?mode=participants`,
                );
                e.stopPropagation();
              }}
            >
            </x-data-view>
          </x-flex>
        `;
      },
      { forbiddenTemplate: nothing, context: institutionID ?? GLOBAL_CONTEXT },
    )}
  `;
});

/**
 * @element bh-page-admin-einrichtung-counselings
 */
@customElement('bh-page-admin-einrichtung-counselings')
export class BHPageAdminEinrichtungCounselings extends BuchungsheldLitElement {
  override render() {
    return html` ${watch(adminEinrichtungCounselingsRouter.outlet)} `;
  }
}

const renderOptions = (instance: EinzelberatungDataview, design: ThemeDefinition, params: Record<string, string>) => {
  return html`
    <x-flex space="xs" horizontal crossaxis-start>
      <tr-button-small
        icon="edit"
        .action=${e => {
          console.log('edit', params, instance);
          goTo(`/admin/institution-details/${params.institutionID}/counselings/detail/${instance.einzelberatung._id}`);
          e.stopPropagation();
          e.preventDefault();
        }}
        .color=${design?.colors.secondary.shade(200)}
      ></tr-button-small>

      <tr-button-small
        .color=${design?.colors.tone.error}
        icon="delete"
        .action=${async () => {
          const confirmText = clientTranslator.translate('counseling', 'delete_termin_confirmation');
          const confirmed = await XDialog.confirm(confirmText);

          if (!confirmed) return;

          await Einzelberatung.removeEinzelberatungByID(instance.einzelberatung._id)();
        }}
        .color=${design?.colors.secondary.shade(200)}
      ></tr-button-small>
    </x-flex>
  `;
};
